import type { UploadProgressEvent, UploadRequestOptions } from 'element-plus'
import type { ImageResource, VideoResource } from '../types/material'
import type { MsUploadResource } from '../types/ms-upload'
import { createResource, fetchUploadManage, fetchUploadToken } from '../api/material'
import { showErrorMessage, UploadType } from '../utils'

interface CreateResource {
  Image: (file: File) => Promise<Pick<ImageResource, 'height' | 'width'>>
  Audio: (file: File) => Promise<Pick<AudioBuffer, 'duration'>>
  Video: (file: File) => Promise<Pick<VideoResource, 'duration' | 'width' | 'poster' | 'height'>>
}

export function useUploadFile(
  alias: string,
  tokenApiPath: string,
  resourceApiPath: string,
  autoCreateResource: boolean,
): { toUploadFile: (options: UploadRequestOptions) => Promise<void> } {
  const { createAudioResource } = useGetAudioDuration()
  const { createImageResource } = useGetImageWH()
  const { createVideoResource } = useGetVideoDuration(tokenApiPath)

  function getResourceInfo(fileType: string): {
    tp: UploadType.image | UploadType.video | UploadType.audio | UploadType.application
    createResourceInfo?: CreateResource['Image'] | CreateResource['Video'] | CreateResource['Audio']
  } {
    if (fileType.includes('image')) {
      return {
        tp: UploadType.image,
        createResourceInfo: createImageResource,
      }
    }
    if (fileType.includes('audio')) {
      return {
        tp: UploadType.audio,
        createResourceInfo: createAudioResource,
      }
    }
    if (fileType.includes('video')) {
      return {
        tp: UploadType.video,
        createResourceInfo: createVideoResource,
      }
    }
    return {
      tp: UploadType.application,
    }
  }

  async function toUploadFile(options: UploadRequestOptions): Promise<void> {
    const { file } = options
    try {
      const { file } = options
      const { tp, createResourceInfo } = getResourceInfo(file.type)
      let resource: Partial<MsUploadResource> = {}
      if (tp !== UploadType.application) {
        const resourceInfo = await createResourceInfo?.(file)
        if (resourceInfo !== undefined) {
          resource = resourceInfo
        }
      }
      const token = await fetchUploadToken(tokenApiPath)
      const fileName = file.name.replace(/([%+])/g, (_$0, $1) => {
        return { '%': 'CiU=', '+': 'Cis=' }[$1]
      })
      const url = `${token.host}/${token.path}/${fileName}`
      await fetchUploadManage(fileName, token, options.file, (progress: UploadProgressEvent) => {
        progress.percent = (progress.loaded / progress.total) * 100
        options.onProgress(progress)
      })
      resource.url = url
      resource.size = file.size
      resource.name = file.name
      resource.tp = tp
      resource.alias = alias
      const fileUploadRes = autoCreateResource ? await createResource(resourceApiPath, resource) : undefined
      options.onSuccess({
        ...resource,
        id: fileUploadRes?.data?.id,
      })
    } catch (e) {
      options.onError({
        message: '',
        stack: '',
        name: file?.name,
        status: 0,
        url: '',
        method: '',
      })
      console.log('err==>', e)
      showErrorMessage(`上传 "${file?.name ? file.name : '文件'}" 失败，请重新上传 !`)
    }
  }

  return { toUploadFile }
}

function useGetAudioDuration(): { createAudioResource: CreateResource['Audio'] } {
  const createAudioResource: CreateResource['Audio'] = async (file) => {
    const duration = await readAudioDuration(URL.createObjectURL(file))
    return {
      duration,
    }
  }

  async function readAudioDuration(url: string): Promise<number> {
    return new Promise((resolve: (value: number) => void, reject: (ev: ErrorEvent) => void) => {
      const audio = new Audio(url)

      function loadedmetadata(): void {
        audio.removeEventListener('error', errored)
        resolve(Math.floor(audio.duration))
      }

      function errored(ev: ErrorEvent): void {
        audio.removeEventListener('loadedmetadata', loadedmetadata)
        reject(ev)
      }

      audio.addEventListener('loadedmetadata', loadedmetadata)
      audio.addEventListener('error', errored)
    })
  }

  return {
    createAudioResource,
  }
}

function useGetImageWH(): { createImageResource: CreateResource['Image'] } {
  const createImageResource: CreateResource['Image'] = async (file) => {
    return readWH(URL.createObjectURL(file))
  }

  async function readWH(url: string): Promise<Pick<ImageResource, 'width' | 'height'>> {
    return new Promise((resolve: (value: Pick<ImageResource, 'width' | 'height'>) => void, reject: (e?: Error) => void) => {
      const img = new Image()
      img.onload = () => {
        const width = Math.floor(img.naturalWidth)
        const height = Math.floor(img.naturalHeight)
        if (width === 0 || height === 0) {
          reject(new Error('图片文件错误'))
        } else {
          resolve({
            width,
            height,
          })
        }
      }
      img.onerror = () => {
        reject(new Error('图片文件错误'))
      }
      img.src = url
    })
  }

  return {
    createImageResource,
  }
}

function useGetVideoDuration(tokenApiPath: string): { createVideoResource: CreateResource['Video'] } {
  function imageBase64toBlob(dataURI: string): Blob {
    const byteString = window.atob(dataURI.split(',')[1])
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]
    const ab = new ArrayBuffer(byteString.length)
    const ia = new Uint8Array(ab)
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i)
    }
    return new Blob([ia], { type: mimeString })
  }

  async function getPosterUrl(video: HTMLVideoElement, fileName: string): Promise<string | undefined> {
    const scale = 0.5
    try {
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth * scale
      canvas.height = video.videoHeight * scale
      canvas?.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height)
      const imgBase64 = canvas.toDataURL('image/png')
      const token = await fetchUploadToken(tokenApiPath)
      const name = `${fileName}.png`
      const url = `${token.host}/${token.path}/${name}`
      await fetchUploadManage(name, token, <File>imageBase64toBlob(imgBase64))
      return url
    } catch (e) {
      console.error(e)
    }
  }

  const readVideoDuration: CreateResource['Video'] = async (file: File) => {
    return new Promise((resolve: (value: Pick<VideoResource, 'duration' | 'width' | 'poster' | 'height'>) => void, reject: (e: Error | ErrorEvent) => void) => {
      const video = document.createElement('video')
      const source = document.createElement('source')
      video.muted = true
      video.autoplay = true
      source.src = URL.createObjectURL(file)
      source.type = file.type
      video.appendChild(source)

      async function canplay(): Promise<void> {
        video.removeEventListener('error', errored)
        if (video.videoWidth === 0 || video.videoHeight === 0) {
          reject(new Error('视频文件错误'))
          return
        }
        const resource = {
          duration: Math.floor(video.duration),
          width: video.videoWidth,
          height: video.videoHeight,
          poster: await getPosterUrl(video, file.name),
        }
        video.removeChild(source)
        resolve(resource)
      }

      function errored(ev: ErrorEvent): void {
        video.removeEventListener('canplay', canplay)
        reject(ev)
      }

      video.addEventListener('canplay', canplay)
      video.addEventListener('error', errored)
    },
    )
  }

  const createVideoResource: CreateResource['Video'] = async (file) => {
    return readVideoDuration(file)
  }

  return {
    createVideoResource,
  }
}
